import classnames from "classnames";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import CountDownDialog from "../CountDownDialog/CountDownDialog";
import { Keyboard } from "../Keyboard";
import OkButton from "../OkButton/OkButton";
import ValidationErrorDialog from "../ValidationErrorDialog/ValidationErrorDialog";
import { PinValidationStatus } from "./PinPad";
import { PinPadKeyboardLayout } from "./PinPadKeyboardLayout";
import { isResultOk } from "./pinValidation";

// tslint:disable-next-line:no-var-requires
const style = require("./PinPadView.scss");

export interface PinPadViewProps {
    readonly pinValue: string;
    readonly validationStatus?: PinValidationStatus;
    readonly lastSubmitTimestamp: number;
    submit(): void;
    pinChange(newPinValue: string): void;
}

type Props = PinPadViewProps & WithTranslation;

export function PinPadView({
    t,
    validationStatus,
    lastSubmitTimestamp,
    pinValue,
    pinChange,
    submit
}: Props) {
    const validationDialogsProps = {
        lastSubmitTimestamp,
        t,
        validationStatus
    };
    return (
        <div className={classnames(style.pinScreen)}>
            {renderPinKeyboardPanel({
                pinValue,
                pinChange,
                submit,
                validationStatus
            })}
            <div className={classnames(style.activationHelp)}>
                {t("activationHelp")}
            </div>
            {renderValidationDialogs(validationDialogsProps)}
            {renderBlockCountdown(validationDialogsProps)}
        </div>
    );
}

function renderValidationDialogs({
    validationStatus,
    lastSubmitTimestamp,
    t
}: Pick<Props, "validationStatus" | "lastSubmitTimestamp" | "t">) {
    const pinIsValid =
        isResultOk(validationStatus) ||
        validationStatus === "PIN_RETRY_REACHED";
    return (
        <ValidationErrorDialog
            isValid={pinIsValid}
            validationTimestamp={lastSubmitTimestamp}
            title={t("Pin validation")}
        >
            <p>{t(getValidationMessage(validationStatus))}</p>
        </ValidationErrorDialog>
    );
}

function renderBlockCountdown({
    validationStatus,
    lastSubmitTimestamp,
    t
}: Pick<Props, "validationStatus" | "lastSubmitTimestamp" | "t">) {
    return (
        validationStatus === "PIN_RETRY_REACHED" && (
            <CountDownDialog
                key={lastSubmitTimestamp}
                seconds={30}
                title={t("Pin validation")}
            >
                {seconds => <p>{t("machineBlocked", { seconds })}</p>}
            </CountDownDialog>
        )
    );
}

const VALIDATION_MESSAGE_MAPPING: { [K in PinValidationStatus]?: string } = {
    PIN_CHANGE_ERROR: "message",
    PIN_INVALID: "message",
    PIN_LENGTH_ERROR: "wrongLength",
    PIN_RETRY_REACHED: "machineBlocked",
    PIN_TOO_SIMPLE: "blockedPIN"
};
function getValidationMessage(validationStatus?: PinValidationStatus) {
    if (validationStatus) {
        return VALIDATION_MESSAGE_MAPPING[validationStatus] || "message";
    }
    return "";
}

function renderPinKeyboardPanel(
    props: Pick<
        PinPadViewProps,
        "pinValue" | "pinChange" | "submit" | "validationStatus"
    >
) {
    const input = React.createRef<HTMLInputElement>();
    return (
        <div className={classnames(style.pinPad)}>
            <input
                type="password"
                value={props.pinValue}
                // tslint:disable-next-line:jsx-no-lambda
                onChange={e => props.pinChange(e.target.value)}
                maxLength={4}
                ref={input}
            />

            <Keyboard input={input}>
                <PinPadKeyboardLayout />
            </Keyboard>
            <OkButton
                isActive={props.validationStatus === "PIN_OK"}
                click={props.submit}
            />
        </div>
    );
}

export default withTranslation("pinPad")(PinPadView);
